import {
  GENERATION_IMPACT_MODEL_VERSION,
  GENERATION_IMPACT_SCHEMA_VERSION,
  type EngineeringVerdict,
  type GenerationImpactResult,
  type GenerationImpactScenarioInput,
  type GenerationImpactSnapshot,
  type GenerationNoiseImpactResult,
  type GenerationNoiseReceptor,
  type GenerationNoiseScenario,
  type GenerationNoiseUnit,
  type GenerationUnit,
  type GenerationUnitResult,
  type NoiseComponentResult,
  type NoiseContourResult,
  type NoiseDatum,
} from '../../types/generationImpact';
import { deriveGasInterconnectDerate } from './gasInterconnectDerate';
import { buildNoiseField } from './noiseField';

const CONTOUR_LEVELS = [45, 50, 55, 60, 65] as const;
const TEN_FEET_M = 3.048;
const NOISE_CAVEATS = [
  'Free-field geometric-spreading screen only; this is not an ISO 9613 compliance model.',
  'Ground absorption, atmospheric absorption, terrain, reflections, tonal penalties, weather and operating duty are excluded unless represented by an explicit barrier loss.',
  'Reference-SPL inputs are not extrapolated inside their measurement/reference distance.',
] as const;

function assertFiniteInRange(
  value: number,
  label: string,
  min: number,
  max: number,
): void {
  if (!Number.isFinite(value) || value < min || value > max) {
    throw new RangeError(`${label} must be finite and between ${min} and ${max}; received ${value}`);
  }
}
function validateLoss(value: number | undefined, label: string): number {
  if (value == null) return 0;
  assertFiniteInRange(value, label, 0, 100);
  return value;
}

function validateDatum(datum: NoiseDatum, unitName: string): void {
  assertFiniteInRange(datum.levelDbA, `${unitName} noise level`, -50, 250);
  if (datum.kind === 'sound-power') {
    assertFiniteInRange(
      datum.directivityCorrectionDb ?? 0,
      `${unitName} directivity correction`,
      -50,
      50,
    );
  } else {
    assertFiniteInRange(
      datum.referenceDistanceM,
      `${unitName} reference distance`,
      0.01,
      100_000,
    );
  }
}

function validateUnit(unit: GenerationNoiseUnit): void {
  assertFiniteInRange(unit.count, `${unit.name} count`, 0, 1_000_000);
  if (!Number.isInteger(unit.count)) {
    throw new RangeError(`${unit.name} count must be an integer; received ${unit.count}`);
  }
  validateLoss(unit.barrierLossDb, `${unit.name} source barrier loss`);
  if (unit.noise) validateDatum(unit.noise, unit.name);
  if (!unit.noise && unit.soundPowerDbA != null) {
    assertFiniteInRange(unit.soundPowerDbA, `${unit.name} legacy sound power`, -50, 250);
    assertFiniteInRange(
      unit.directivityCorrectionDb ?? 0,
      `${unit.name} legacy directivity correction`,
      -50,
      50,
    );
  }
}

function validateReceptor(receptor: GenerationNoiseReceptor): void {
  assertFiniteInRange(receptor.distanceM, `${receptor.name} distance`, 0.01, 1_000_000);
  validateLoss(receptor.barrierLossDb, `${receptor.name} barrier loss`);
  if (receptor.thresholdDbA != null) {
    assertFiniteInRange(receptor.thresholdDbA, `${receptor.name} threshold`, -50, 200);
  }
}

function countCorrectionDb(unit: GenerationNoiseUnit, datum: NoiseDatum | null): number {
  if (unit.count <= 0) return Number.NEGATIVE_INFINITY;
  if (datum?.kind === 'reference-spl' && datum.scope === 'aggregate') return 0;
  return 10 * Math.log10(unit.count);
}

function resolvedDatum(unit: GenerationNoiseUnit): {
  datum: NoiseDatum | null;
  basis: NoiseComponentResult['basis'];
} {
  if (unit.noise) {
    return { datum: unit.noise, basis: unit.noise.kind };
  }
  if (unit.soundPowerDbA != null) {
    return {
      datum: {
        kind: 'sound-power',
        levelDbA: unit.soundPowerDbA,
        directivityCorrectionDb: unit.directivityCorrectionDb,
      },
      basis: 'legacy-sound-power',
    };
  }
  return { datum: null, basis: 'sound-power' };
}

/** Logarithmically combine independent A-weighted levels. */
export function combineNoiseLevelsDbA(levels: readonly number[]): number | null {
  const finite = levels.filter(Number.isFinite);
  if (finite.length === 0) return null;
  const energy = finite.reduce((sum, level) => sum + 10 ** (level / 10), 0);
  return 10 * Math.log10(energy);
}

/**
 * Calculate one source at one receptor. Sound-power behavior intentionally
 * preserves the legacy equation exactly:
 *
 *   Lp = Lw + Dc - 10 log10(4 pi r^2) - losses + 10 log10(count)
 */
function componentAtDistance(
  unit: GenerationNoiseUnit,
  receptor: GenerationNoiseReceptor,
): { result: NoiseComponentResult; energy: number | null } {
  const { datum, basis } = resolvedDatum(unit);
  if (unit.count === 0 || datum == null) {
    return {
      result: {
        unitId: unit.id,
        unitName: unit.name,
        levelDbA: null,
        status: 'missing-input',
        basis,
      },
      energy: null,
    };
  }

  const losses = validateLoss(unit.barrierLossDb, `${unit.name} source barrier loss`)
    + validateLoss(receptor.barrierLossDb, `${receptor.name} barrier loss`);

  if (datum.kind === 'reference-spl') {
    if (receptor.distanceM < datum.referenceDistanceM) {
      return {
        result: {
          unitId: unit.id,
          unitName: unit.name,
          levelDbA: null,
          status: 'inside-reference-distance',
          basis,
        },
        energy: null,
      };
    }
    const baseLevel = datum.levelDbA
      - 20 * Math.log10(receptor.distanceM / datum.referenceDistanceM)
      - losses;
    const energy = datum.scope === 'aggregate'
      ? 10 ** (baseLevel / 10)
      : unit.count * 10 ** (baseLevel / 10);
    return {
      result: {
        unitId: unit.id,
        unitName: unit.name,
        levelDbA: 10 * Math.log10(energy),
        status: 'calculated',
        basis,
      },
      energy,
    };
  }

  const baseLevel = datum.levelDbA
    + (datum.directivityCorrectionDb ?? 0)
    - 10 * Math.log10(4 * Math.PI * receptor.distanceM * receptor.distanceM)
    - losses;
  const energy = unit.count * 10 ** (baseLevel / 10);
  return {
    result: {
      unitId: unit.id,
      unitName: unit.name,
      // Preserve the legacy v1 operation order: multiply acoustic energy by
      // count before converting the combined energy back to decibels.
      levelDbA: 10 * Math.log10(energy),
      status: 'calculated',
      basis,
    },
    energy,
  };
}

function equivalentLevelAtOneMetre(unit: GenerationNoiseUnit): number | null {
  const { datum } = resolvedDatum(unit);
  if (unit.count === 0 || datum == null) return null;
  const loss = validateLoss(unit.barrierLossDb, `${unit.name} source barrier loss`);
  const countDb = countCorrectionDb(unit, datum);
  if (datum.kind === 'reference-spl') {
    return datum.levelDbA + 20 * Math.log10(datum.referenceDistanceM) - loss + countDb;
  }
  return datum.levelDbA
    + (datum.directivityCorrectionDb ?? 0)
    - 10 * Math.log10(4 * Math.PI)
    - loss
    + countDb;
}

function contoursForUnits(units: readonly GenerationNoiseUnit[]): NoiseContourResult[] {
  const oneMetreLevels = units
    .map(equivalentLevelAtOneMetre)
    .filter((level): level is number => level != null && Number.isFinite(level));
  const combinedAtOneMetre = combineNoiseLevelsDbA(oneMetreLevels);
  const activeReferenceDistances = units.flatMap(unit => {
    const { datum } = resolvedDatum(unit);
    return unit.count > 0 && datum?.kind === 'reference-spl'
      ? [datum.referenceDistanceM]
      : [];
  });
  const minimumValidDistanceM = activeReferenceDistances.length > 0
    ? Math.max(...activeReferenceDistances)
    : 0;

  return CONTOUR_LEVELS.map(levelDbA => {
    if (combinedAtOneMetre == null) {
      return { levelDbA, radiusM: null, status: 'missing-input' };
    }
    const radiusM = 10 ** ((combinedAtOneMetre - levelDbA) / 20);
    if (!Number.isFinite(radiusM) || radiusM < minimumValidDistanceM) {
      return { levelDbA, radiusM: null, status: 'inside-reference-distance' };
    }
    return { levelDbA, radiusM, status: 'calculated' };
  });
}

/** Deterministic free-field acoustic screening calculation. */
export function solveGenerationNoise(
  scenario: GenerationNoiseScenario,
): GenerationNoiseImpactResult {
  for (const unit of scenario.units) validateUnit(unit);
  for (const receptor of scenario.receptors) validateReceptor(receptor);

  const receptors = scenario.receptors.map(receptor => {
    const calculations = scenario.units.map(unit => componentAtDistance(unit, receptor));
    const components = calculations.map(calculation => calculation.result);
    const hasNearFieldUnknown = components.some(c => c.status === 'inside-reference-distance');
    const totalEnergy = calculations.reduce(
      (sum, calculation) => sum + (calculation.energy ?? 0),
      0,
    );
    const predictedDbA = hasNearFieldUnknown || totalEnergy <= 0
      ? null
      : 10 * Math.log10(totalEnergy);
    const thresholdDbA = receptor.thresholdDbA ?? null;
    const marginDb = predictedDbA != null && thresholdDbA != null
      ? thresholdDbA - predictedDbA
      : null;
    const verdict = predictedDbA == null || thresholdDbA == null
      ? 'indeterminate' as const
      : predictedDbA <= thresholdDbA
        ? 'pass' as const
        : 'exceedance' as const;
    return {
      receptorId: receptor.id,
      receptorName: receptor.name,
      distanceM: receptor.distanceM,
      predictedDbA,
      thresholdDbA,
      marginDb,
      verdict,
      components,
    };
  });

  return {
    modelBasis: 'free-field-screening',
    modelVersion: 'free-field-screening-v1',
    receptors,
    contours: contoursForUnits(scenario.units),
    caveats: [...NOISE_CAVEATS],
  };
}

/**
 * User-supplied planning assumption requested for the first screening case.
 * It is aggregate by design, so equipment count never multiplies the 85 dBA.
 */
export function create85DbAt10FtPlanningScenario(
  receptors: GenerationNoiseReceptor[] = [],
  center?: { lat: number; lng: number },
): GenerationNoiseScenario {
  return {
    id: 'planning-85dba-at-10ft',
    name: 'Planning assumption — 85 dBA at 10 ft',
    units: [
      {
        id: 'aggregate-generation-source',
        name: 'Aggregate generation source',
        count: 1,
        noise: {
          kind: 'reference-spl',
          levelDbA: 85,
          referenceDistanceM: TEN_FEET_M,
          scope: 'aggregate',
          basisLabel: 'User-supplied planning assumption: 85 dBA at 10 ft',
        },
      },
    ],
    receptors,
    ...(center ? { center } : {}),
    notes: 'Screening assumption only; replace with vendor octave-band or sound-power data for design.',
  };
}

/** Adapt the canonical workbench input to the single acoustic kernel. */
export function toGenerationNoiseScenario(
  input: GenerationImpactScenarioInput,
): GenerationNoiseScenario {
  const units = input.noiseSources !== undefined
    ? input.noiseSources.map(source => ({ ...source, noise: source.noise && { ...source.noise } }))
    : input.units.map(unit => ({
        id: unit.id,
        name: unit.label,
        count: unit.count,
        noise: unit.noise && { ...unit.noise },
        soundPowerDbA: unit.soundPowerDbA,
        directivityCorrectionDb: unit.directivityCorrectionDb,
        barrierLossDb: unit.barrierLossDb,
      }));
  return {
    id: `workbench:${input.siteId}:${input.targetPhaseId ?? 'site'}`,
    name: input.name,
    units,
    receptors: input.receptors.map(receptor => ({
      id: receptor.id,
      name: receptor.label,
      distanceM: receptor.distanceM,
      barrierLossDb: receptor.barrierLossDb,
      thresholdDbA: receptor.thresholdDbA,
    })),
    ...(input.layoutAnchor ? { center: { ...input.layoutAnchor } } : {}),
  };
}

/** Prompt block containing only frozen deterministic outputs. */
export function buildGenerationImpactPromptBlock(
  snapshot: GenerationImpactSnapshot,
): string {
  // grid-screening-v2 opt-in: frozen propagation field on the scenario result.
  // Legacy snapshots keep the exact v1 prompt text below.
  const noiseField = snapshot.scenario.result.noiseField;
  const lines = [
    '[NOISE IMPACT — FROZEN DETERMINISTIC SNAPSHOT]',
    `Scenario: ${snapshot.scenario.name}`,
    noiseField
      ? 'Model basis: grid-screening propagation field (single 500 Hz band with atmospheric, ground and Maekawa barrier terms; NOT ISO 9613-2 compliance). Quote these results; do not recompute or invent propagation values.'
      : 'Model basis: free-field screening (NOT ISO 9613 compliance). Quote these results; do not recompute or invent propagation values.',
  ];
  for (const receptor of snapshot.noise.receptors) {
    const predicted = receptor.predictedDbA == null
      ? 'indeterminate'
      : `${receptor.predictedDbA.toFixed(1)} dBA`;
    const threshold = receptor.thresholdDbA == null
      ? 'threshold not supplied'
      : `threshold ${receptor.thresholdDbA.toFixed(1)} dBA`;
    const margin = receptor.marginDb == null
      ? 'margin indeterminate'
      : `margin ${receptor.marginDb.toFixed(1)} dB (${receptor.marginDb >= 0 ? 'below' : 'above'} threshold)`;
    lines.push(
      `- ${receptor.receptorName}: ${predicted} at ${receptor.distanceM.toFixed(1)} m; ${threshold}; ${margin}; verdict ${receptor.verdict}.`,
    );
  }
  const contourText = snapshot.noise.contours
    .map(c => c.radiusM == null
      ? `${c.levelDbA} dBA: ${c.status}`
      : `${c.levelDbA} dBA: ${c.radiusM.toFixed(1)} m radius`)
    .join(' | ');
  lines.push(`Radial contours: ${contourText}`);
  if (noiseField) {
    const bandText = noiseField.bands
      .map(band => `${band.levelDbA} dBA: ${band.rings.length} region${band.rings.length === 1 ? '' : 's'}`)
      .join(' | ');
    const barrierCount = snapshot.scenario.input.layout?.acousticBarriers?.length ?? 0;
    lines.push(`Propagation field (${noiseField.modelVersion}): ${noiseField.gridColumns}×${noiseField.gridRows} grid at ${noiseField.gridSpacingM.toFixed(1)} m spacing; bands — ${bandText === '' ? 'none above 45 dBA in the modeled area' : bandText}.`);
    lines.push(`Acoustic barriers modeled: ${barrierCount}.`);
    lines.push(...noiseField.assumptions.map(a => `Assumption: ${a}`));
  }
  lines.push(...snapshot.noise.caveats.map(c => `Caveat: ${c}`));
  lines.push('[/NOISE IMPACT]');
  return `${lines.join('\n')}\n\n`;
}
const DEFAULT_RESERVE_MARGIN_PCT = 15;
const KG_CO2_PER_MMBTU_NATURAL_GAS_HHV = 53.06;
const KG_PER_METRIC_TONNE = 1_000;
const LB_PER_METRIC_TONNE = 2_204.6226218;
const HOURS_PER_DAY = 24;
/**
 * Largest-contingency share of normal net output above which the fleet is
 * flagged as concentrated. Two equal blocks sit at 0.50 (a full spare); three
 * equal blocks at 0.33, which is not flagged.
 */
const CONTINGENCY_CONCENTRATION_SHARE = 0.4;

function finiteNonNegative(value: number, label: string, gaps: string[]): number | null {
  if (!Number.isFinite(value) || value < 0) {
    gaps.push(`${label} must be a finite, non-negative value.`);
    return null;
  }
  return value;
}

function finitePositive(value: number, label: string, gaps: string[]): number | null {
  if (!Number.isFinite(value) || value <= 0) {
    gaps.push(`${label} must be a finite value greater than zero.`);
    return null;
  }
  return value;
}

function factor(value: number, label: string, gaps: string[]): number | null {
  if (!Number.isFinite(value) || value <= 0 || value > 1) {
    gaps.push(`${label} must be greater than 0 and no greater than 1.`);
    return null;
  }
  return value;
}

function verdictFor(margin: number, determinate: boolean): EngineeringVerdict {
  if (!determinate || !Number.isFinite(margin)) return 'indeterminate';
  return margin >= 0 ? 'pass' : 'fail';
}

/**
 * Exact v1 workbench fallback. Keep the arithmetic and operation order
 * unchanged so legacy persisted inputs retain byte-stable predicted levels.
 */
function legacyWorkbenchNoiseDbA(
  input: GenerationImpactScenarioInput,
  receptor: GenerationImpactScenarioInput['receptors'][number],
): number | null {
  if (!Number.isFinite(receptor.distanceM) || receptor.distanceM <= 0) return null;
  let energy = 0;
  for (const unit of input.units) {
    if (unit.soundPowerDbA == null || !Number.isFinite(unit.soundPowerDbA)) continue;
    const count = Math.max(0, Math.trunc(unit.count));
    if (count === 0) continue;
    const directivity = unit.directivityCorrectionDb ?? 0;
    const barrier = Math.max(0, receptor.barrierLossDb || 0);
    const level = unit.soundPowerDbA + directivity
      - 10 * Math.log10(4 * Math.PI * receptor.distanceM * receptor.distanceM)
      - barrier;
    energy += count * 10 ** (level / 10);
  }
  return energy > 0 ? 10 * Math.log10(energy) : null;
}

function usesExactLegacyWorkbenchNoise(input: GenerationImpactScenarioInput): boolean {
  return input.noiseSources === undefined
    && input.units.every(unit => unit.noise === undefined && unit.barrierLossDb === undefined);
}

function unavailableNoiseProjection(input: GenerationImpactScenarioInput): GenerationNoiseImpactResult {
  return {
    modelBasis: 'free-field-screening',
    modelVersion: 'free-field-screening-v1',
    receptors: input.receptors.map(receptor => ({
      receptorId: receptor.id,
      receptorName: receptor.label,
      distanceM: receptor.distanceM,
      predictedDbA: null,
      thresholdDbA: receptor.thresholdDbA ?? null,
      marginDb: null,
      verdict: 'indeterminate',
      components: [],
    })),
    contours: CONTOUR_LEVELS.map(levelDbA => ({
      levelDbA,
      radiusM: null,
      status: 'missing-input',
    })),
    caveats: [...NOISE_CAVEATS],
  };
}

function probabilityLoadServed(
  input: GenerationImpactScenarioInput,
  netByUnit: Array<{ netMW: number; forcedOutageRate?: number }>,
  criticalLoadMW: number,
): number | null {
  const groups = netByUnit.map((entry, index) => ({ ...entry, count: Math.max(0, Math.trunc(input.units[index]?.count ?? 0)) }));
  if (groups.length === 0 || groups.some((group) => group.forcedOutageRate == null)) return null;
  if (groups.some((group) => !Number.isFinite(group.forcedOutageRate) || group.forcedOutageRate! < 0 || group.forcedOutageRate! > 1)) {
    return null;
  }

  let states = new Map<number, number>([[0, 1]]);
  for (const group of groups) {
    const next = new Map<number, number>();
    const availability = 1 - group.forcedOutageRate!;
    const onlineCountProbability: number[] = group.forcedOutageRate === 0
      ? Array.from({ length: group.count + 1 }, (_, online) => online === group.count ? 1 : 0)
      : [group.forcedOutageRate! ** group.count];
    if (group.forcedOutageRate !== 0) for (let online = 1; online <= group.count; online += 1) {
      const previous = onlineCountProbability[online - 1]!;
      onlineCountProbability.push(previous * (group.count - online + 1) / online * availability / group.forcedOutageRate!);
    }
    for (const [mwKey, probability] of states) {
      for (let online = 0; online <= group.count; online += 1) {
        const onlineKey = Math.round((mwKey + online * group.netMW) * 1_000) / 1_000;
        next.set(onlineKey, (next.get(onlineKey) ?? 0) + probability * onlineCountProbability[online]!);
      }
    }
    if (next.size > 100_000) return null;
    states = next;
  }
  let served = 0;
  for (const [mw, probability] of states) if (mw >= criticalLoadMW) served += probability;
  return served;
}

function solveWorkbenchGenerationImpact(input: GenerationImpactScenarioInput): GenerationImpactResult {
  const gaps: string[] = [];
  const warnings: string[] = [];
  const criticalLoadMW = finiteNonNegative(input.criticalLoadMW ?? input.facilityLoadMW, 'Critical load', gaps) ?? 0;
  const facilityLoadMW = finiteNonNegative(input.facilityLoadMW, 'Facility load', gaps) ?? 0;
  const reserveMarginPct = Number.isFinite(input.reserveMarginPct)
    ? Math.max(0, input.reserveMarginPct!)
    : DEFAULT_RESERVE_MARGIN_PCT;
  const contingencyOutages = Number.isInteger(input.contingencyOutages) && input.contingencyOutages! >= 0
    ? input.contingencyOutages!
    : 1;
  const annualOperatingHours = finiteNonNegative(input.annualOperatingHours, 'Annual operating hours', gaps) ?? 0;
  const availableGasCapacityDthPerDay = input.availableGasCapacityDthPerDay == null ? null : finiteNonNegative(input.availableGasCapacityDthPerDay, 'Available gas capacity', gaps);
  const availableWaterConsumptionGalPerDay = input.availableWaterConsumptionGalPerDay == null ? null : finiteNonNegative(input.availableWaterConsumptionGalPerDay, 'Available water consumption capacity', gaps);
  const ccsExportCapacityTonnesPerYear = input.ccsExportCapacityTonnesPerYear == null ? null : finiteNonNegative(input.ccsExportCapacityTonnesPerYear, 'CCS export capacity', gaps);
  if (annualOperatingHours > 8_760) gaps.push('Annual operating hours cannot exceed 8,760.');

  const generationWithdrawalGalPerMWhGross = finiteNonNegative(
    input.water.generationWithdrawalGalPerMWhGross,
    'Generation water withdrawal intensity',
    gaps,
  ) ?? 0;
  const generationConsumptionGalPerMWhGross = finiteNonNegative(
    input.water.generationConsumptionGalPerMWhGross,
    'Generation water consumption intensity',
    gaps,
  ) ?? 0;
  const generationDesignDay = input.water.generationDesignDay;
  const generationDesignDayWithdrawalGalPerDay = generationDesignDay == null
    ? null
    : finiteNonNegative(
        generationDesignDay.withdrawalGalPerDay,
        'Generation design-day water withdrawal',
        gaps,
      );
  const generationDesignDayConsumptionGalPerDay = generationDesignDay == null
    ? null
    : finiteNonNegative(
        generationDesignDay.consumptionGalPerDay,
        'Generation design-day water consumption',
        gaps,
      );
  if (generationDesignDay != null) {
    finitePositive(
      generationDesignDay.referenceGrossMWhPerDay,
      'Generation design-day reference gross energy',
      gaps,
    );
    if (
      generationDesignDayWithdrawalGalPerDay != null
      && generationDesignDayConsumptionGalPerDay != null
      && generationDesignDayWithdrawalGalPerDay < generationDesignDayConsumptionGalPerDay
    ) {
      gaps.push('Generation design-day water withdrawal cannot be below consumption.');
    }
  }
  const dataCenterWithdrawalGalPerMWhFacility = input.water.dataCenterWithdrawalGalPerMWhFacility == null
    ? null
    : finiteNonNegative(input.water.dataCenterWithdrawalGalPerMWhFacility, 'Data-center water withdrawal intensity', gaps);
  const dataCenterConsumptionGalPerMWhFacility = input.water.dataCenterConsumptionGalPerMWhFacility == null
    ? null
    : finiteNonNegative(input.water.dataCenterConsumptionGalPerMWhFacility, 'Data-center water consumption intensity', gaps);
  const plantAcresPerGrossMW = finiteNonNegative(input.footprint.plantAcresPerGrossMW, 'Plant footprint intensity', gaps) ?? 0;
  const installedPlantAcres = input.footprint.installedPlantAcres == null
    ? null
    : finiteNonNegative(
        input.footprint.installedPlantAcres,
        'Absolute installed plant footprint',
        gaps,
      );
  // NOTE — these two `finiteNonNegative` calls must stay in this exact position
  // and order: they push into `gaps`, and `gaps` is a SERIALIZED array in the
  // result, so reordering them changes `resultHash` for any scenario that has a
  // footprint gap. Only the binding names changed.
  const coolingAcresFixed = finiteNonNegative(input.footprint.coolingAcres, 'Cooling footprint', gaps) ?? 0;
  const switchyardAcresFixed = finiteNonNegative(input.footprint.switchyardAcres, 'Switchyard footprint', gaps) ?? 0;
  // PRESENCE-GATED area intensities. Absent (every scenario written before this
  // field existed) ⇒ `null` ⇒ the flat constants above apply unchanged and no
  // gap is pushed, so legacy results stay byte-identical.
  const coolingAcresPerGrossMW = input.footprint.coolingAcresPerGrossMW === undefined
    ? null
    : finiteNonNegative(input.footprint.coolingAcresPerGrossMW, 'Cooling footprint intensity', gaps);
  const switchyardAcresPerGrossMW = input.footprint.switchyardAcresPerGrossMW === undefined
    ? null
    : finiteNonNegative(input.footprint.switchyardAcresPerGrossMW, 'Switchyard footprint intensity', gaps);

  const captureRate = input.ccs.enabled
    ? factor(input.ccs.captureRate, 'CCS capture rate', gaps) ?? 0
    : 0;
  const ccsSpecificPowerKWhPerTonneCaptured = input.ccs.enabled
    ? finitePositive(input.ccs.specificPowerKWhPerTonneCaptured, 'CCS specific power', gaps) ?? 0
    : 0;
  const ccsWaterWithdrawalGalPerTonneCaptured = input.ccs.enabled
    ? finiteNonNegative(input.ccs.waterWithdrawalGalPerTonneCaptured, 'CCS water withdrawal intensity', gaps) ?? 0
    : 0;
  const ccsWaterConsumptionGalPerTonneCaptured = input.ccs.enabled
    ? finiteNonNegative(input.ccs.waterConsumptionGalPerTonneCaptured, 'CCS water consumption intensity', gaps) ?? 0
    : 0;
  const ccsFootprintAcres = input.ccs.enabled
    ? finiteNonNegative(input.ccs.footprintAcres, 'CCS capture footprint', gaps) ?? 0
    : 0;
  const compressionFootprintAcres = input.ccs.enabled
    ? finiteNonNegative(input.ccs.compressionFootprintAcres, 'CCS compression footprint', gaps) ?? 0
    : 0;

  if (input.criticalLoadMW == null) warnings.push('Critical load defaults to facility load.');
  if (input.reserveMarginPct == null) warnings.push(`Planning reserve defaults to ${DEFAULT_RESERVE_MARGIN_PCT}%.`);
  if (input.contingencyOutages != null && (!Number.isInteger(input.contingencyOutages) || input.contingencyOutages < 0)) {
    gaps.push('Contingency outages must be a non-negative integer.');
  }
  if (input.units.length === 0) gaps.push('At least one generating unit or block is required.');

  const preliminary = input.units.map((unit) => {
    const count = Number.isInteger(unit.count) && unit.count > 0 ? unit.count : 0;
    if (count === 0) gaps.push(`${unit.label}: count must be a positive integer.`);
    const gross = finiteNonNegative(unit.isoGrossMWPerUnit, `${unit.label}: ISO gross output`, gaps) ?? 0;
    const ambient = factor(unit.ambientFactor, `${unit.label}: ambient factor`, gaps) ?? 0;
    const degradation = factor(unit.degradationFactor, `${unit.label}: degradation factor`, gaps) ?? 0;
    const auxiliary = finiteNonNegative(unit.auxiliaryLoadMWPerUnit, `${unit.label}: auxiliary load`, gaps) ?? 0;
    const heatRate = finiteNonNegative(unit.heatRateBtuPerKWhHhv, `${unit.label}: HHV heat rate`, gaps) ?? 0;
    const siteGrossMWPerUnit = gross * ambient * degradation;
    if (auxiliary >= siteGrossMWPerUnit && siteGrossMWPerUnit > 0) {
      gaps.push(`${unit.label}: auxiliary load must be below site-rated gross output.`);
    }
    const grossGasMmbtuPerHour = siteGrossMWPerUnit * heatRate / 1_000;
    const uncontrolledCo2TonnesPerHour = grossGasMmbtuPerHour * KG_CO2_PER_MMBTU_NATURAL_GAS_HHV / KG_PER_METRIC_TONNE;
    return { unit, count, siteGrossMWPerUnit, auxiliary, grossGasMmbtuPerHour, uncontrolledCo2TonnesPerHour };
  });

  const totalCapturedTonnesPerHour = input.ccs.enabled
    ? preliminary.reduce((sum, row) => sum + row.uncontrolledCo2TonnesPerHour * row.count, 0) * captureRate
    : 0;
  if (input.ccs.enabled) {
    if (!input.ccs.pipelineExportConfirmed) warnings.push('CO2 pipeline export capacity is unconfirmed.');
  }
  const totalCcsMW = input.ccs.enabled
    ? totalCapturedTonnesPerHour * ccsSpecificPowerKWhPerTonneCaptured / 1_000
    : 0;
  const totalSiteGrossMW = preliminary.reduce((sum, row) => sum + row.siteGrossMWPerUnit * row.count, 0);

  // HEAT-REJECTION + SWITCHYARD AREA. The flat `coolingAcres` / `switchyardAcres`
  // do NOT scale with capacity — a 160 MW plant and a 400 MW plant are both
  // charged the same figure. That is fine at a scenario's documented rating, but
  // it makes any capacity SWEEP optimistic on land. When an intensity is declared
  // the area scales with SITE-RATED gross MW (the same basis `plantAcres` uses);
  // when it is absent the legacy constant applies verbatim.
  //
  // REPLACEMENT, not additive — the same precedence rule `installedPlantAcres`
  // uses over `plantAcresPerGrossMW`. Additive would double-count, because the
  // existing flat figure already represents the WHOLE area at the current rating.
  const coolingAcres = coolingAcresPerGrossMW == null
    ? coolingAcresFixed
    : totalSiteGrossMW * coolingAcresPerGrossMW;
  const switchyardAcres = switchyardAcresPerGrossMW == null
    ? switchyardAcresFixed
    : totalSiteGrossMW * switchyardAcresPerGrossMW;

  const unitResults: GenerationUnitResult[] = preliminary.map((row) => {
    const allocation = totalSiteGrossMW > 0 ? row.siteGrossMWPerUnit / totalSiteGrossMW : 0;
    const ccsLoadMWPerUnit = totalCcsMW * allocation;
    const netMWPerUnit = row.siteGrossMWPerUnit - row.auxiliary - ccsLoadMWPerUnit;
    if (netMWPerUnit < 0) gaps.push(`${row.unit.label}: calculated net output is negative.`);
    return {
      unitId: row.unit.id,
      label: row.unit.label,
      contingencyGroupId: row.unit.contingencyGroupId,
      count: row.count,
      siteGrossMWPerUnit: row.siteGrossMWPerUnit,
      ccsLoadMWPerUnit,
      netMWPerUnit,
      totalNetMW: netMWPerUnit * row.count,
      peakGasMmbtuPerHour: row.grossGasMmbtuPerHour * row.count,
    };
  });

  const groupNet = new Map<string, number>();
  unitResults.forEach((row) => groupNet.set(row.contingencyGroupId, (groupNet.get(row.contingencyGroupId) ?? 0) + row.totalNetMW));
  const rankedGroups = [...groupNet.entries()].sort((a, b) => b[1] - a[1]);
  const largest = rankedGroups[0] ?? null;
  const normalNetMW = unitResults.reduce((sum, row) => sum + row.totalNetMW, 0);
  const largestContingencyMW = largest?.[1] ?? 0;
  const nMinusOneNetMW = normalNetMW - largestContingencyMW;
  const contingencyLostMW = rankedGroups
    .slice(0, contingencyOutages)
    .reduce((sum, [, groupMW]) => sum + groupMW, 0);
  const contingencyNetMW = normalNetMW - contingencyLostMW;
  if (contingencyOutages > rankedGroups.length && rankedGroups.length > 0) {
    warnings.push(`The selected N-${contingencyOutages} criterion exceeds the ${rankedGroups.length} independent contingency groups configured.`);
  }
  const reserveRequiredMW = criticalLoadMW * (1 + reserveMarginPct / 100);
  const determinate = gaps.length === 0;

  const peakMmbtuPerHour = unitResults.reduce((sum, row) => sum + row.peakGasMmbtuPerHour, 0);
  const annualMmbtu = peakMmbtuPerHour * annualOperatingHours;
  const uncontrolledCo2TonnesPerYear = annualMmbtu * KG_CO2_PER_MMBTU_NATURAL_GAS_HHV / KG_PER_METRIC_TONNE;
  const capturedCo2TonnesPerYear = input.ccs.enabled ? uncontrolledCo2TonnesPerYear * captureRate : 0;
  const residualCo2TonnesPerYear = uncontrolledCo2TonnesPerYear - capturedCo2TonnesPerYear;
  const annualGrossMWh = totalSiteGrossMW * annualOperatingHours;
  const dailyGrossMWh = totalSiteGrossMW * HOURS_PER_DAY;

  const noxRates = preliminary.filter((r) => r.unit.noxLbPerMWh != null);
  const coRates = preliminary.filter((r) => r.unit.coLbPerMWh != null);
  const noxTonnesPerYear = noxRates.length === preliminary.length && preliminary.length > 0
    ? preliminary.reduce((sum, row) => sum + row.siteGrossMWPerUnit * row.count * annualOperatingHours * row.unit.noxLbPerMWh! / LB_PER_METRIC_TONNE, 0)
    : null;
  const coTonnesPerYear = coRates.length === preliminary.length && preliminary.length > 0
    ? preliminary.reduce((sum, row) => sum + row.siteGrossMWPerUnit * row.count * annualOperatingHours * row.unit.coLbPerMWh! / LB_PER_METRIC_TONNE, 0)
    : null;
  if (noxTonnesPerYear == null) warnings.push('NOx emissions are not calculated because a sourced factor is missing.');
  if (coTonnesPerYear == null) warnings.push('CO emissions are not calculated because a sourced factor is missing.');

  // MIXED-FLEET water: when EVERY unit declares its own intensity, water is
  // aggregated per unit (each technology carries its own gal/MWh) instead of
  // applying one plant-wide figure to the whole fleet. A fleet that does not
  // declare them (every legacy scenario) takes the original plant-wide path, so
  // results stay byte-identical. A design-day override still wins over both.
  const perUnitWithdrawal = input.units.length > 0
    && input.units.every(unit => Number.isFinite(unit.generationWithdrawalGalPerMWhGross));
  const perUnitConsumption = input.units.length > 0
    && input.units.every(unit => Number.isFinite(unit.generationConsumptionGalPerMWhGross));
  const perUnitDailyGal = (intensity: (unit: GenerationUnit) => number | undefined): number =>
    preliminary.reduce(
      (sum, row) => sum + row.siteGrossMWPerUnit * row.count * HOURS_PER_DAY * (intensity(row.unit) ?? 0),
      0,
    );
  const generationWithdrawalGalPerDay = generationDesignDay == null
    ? (perUnitWithdrawal
        ? perUnitDailyGal(unit => unit.generationWithdrawalGalPerMWhGross)
        : dailyGrossMWh * generationWithdrawalGalPerMWhGross)
    : generationDesignDayWithdrawalGalPerDay ?? 0;
  const generationConsumptionGalPerDay = generationDesignDay == null
    ? (perUnitConsumption
        ? perUnitDailyGal(unit => unit.generationConsumptionGalPerMWhGross)
        : dailyGrossMWh * generationConsumptionGalPerMWhGross)
    : generationDesignDayConsumptionGalPerDay ?? 0;
  const dailyCapturedTonnes = totalCapturedTonnesPerHour * HOURS_PER_DAY;
  const ccsWithdrawalGalPerDay = input.ccs.enabled ? dailyCapturedTonnes * ccsWaterWithdrawalGalPerTonneCaptured : 0;
  const ccsConsumptionGalPerDay = input.ccs.enabled ? dailyCapturedTonnes * ccsWaterConsumptionGalPerTonneCaptured : 0;
  const dataCenterWithdrawalGalPerDay = dataCenterWithdrawalGalPerMWhFacility == null
    ? null : facilityLoadMW * HOURS_PER_DAY * dataCenterWithdrawalGalPerMWhFacility;
  const dataCenterConsumptionGalPerDay = dataCenterConsumptionGalPerMWhFacility == null
    ? null : facilityLoadMW * HOURS_PER_DAY * dataCenterConsumptionGalPerMWhFacility;

  // MIXED-FLEET plant footprint: same rule as water — per-unit acres/MW when
  // every unit declares one, otherwise the plant-wide intensity (byte-identical
  // for legacy scenarios). An explicit installed acreage still wins over both.
  const perUnitPlantAcres = input.units.length > 0
    && input.units.every(unit => Number.isFinite(unit.plantAcresPerGrossMW));
  const plantAcres = input.footprint.installedPlantAcres == null
    ? (perUnitPlantAcres
        ? preliminary.reduce(
            (sum, row) => sum + row.siteGrossMWPerUnit * row.count * (row.unit.plantAcresPerGrossMW ?? 0),
            0,
          )
        : totalSiteGrossMW * plantAcresPerGrossMW)
    : installedPlantAcres ?? 0;
  const ccsAcres = input.ccs.enabled ? ccsFootprintAcres : 0;
  const compressionAcres = input.ccs.enabled ? compressionFootprintAcres : 0;

  let noiseProjection: GenerationNoiseImpactResult;
  try {
    noiseProjection = solveGenerationNoise(toGenerationNoiseScenario(input));
  } catch (error) {
    warnings.push(`Noise screening is indeterminate: ${(error as Error).message}`);
    noiseProjection = unavailableNoiseProjection(input);
  }
  const exactLegacyNoise = usesExactLegacyWorkbenchNoise(input);

  // grid-screening-v2 — the PRESENCE of frozen `input.noiseGeometry` (with
  // `noiseSources`) opts the scenario into the propagation field. Failure
  // degrades to the v1 projection with a warning. Legacy scenarios can never
  // enter this branch: `usesExactLegacyWorkbenchNoise` requires noiseSources
  // to be ABSENT while the field requires it PRESENT — mutually exclusive by
  // construction, so persisted v1 arithmetic stays byte-exact.
  let noiseField: GenerationImpactResult['noiseField'];
  if (input.noiseGeometry !== undefined && input.noiseSources !== undefined) {
    try {
      noiseField = buildNoiseField({
        noiseSources: input.noiseSources,
        geometry: input.noiseGeometry,
        barriers: input.layout?.acousticBarriers ?? [],
        receptors: input.receptors,
      });
    } catch (error) {
      warnings.push(`Noise propagation field is indeterminate: ${(error as Error).message}`);
    }
  }
  const fieldLevelByReceptorId = new Map(
    (noiseField?.receptorLevels ?? []).map((row) => [row.receptorId, row.predictedDbA]),
  );

  const noise = input.receptors.map((inputReceptor, index) => {
    const receptor = noiseProjection.receptors[index]
      ?? unavailableNoiseProjection({ ...input, receptors: [inputReceptor] }).receptors[0]!;
    const predictedDbA = noiseField
      ? fieldLevelByReceptorId.get(inputReceptor.id) ?? null
      : exactLegacyNoise
        ? legacyWorkbenchNoiseDbA(input, inputReceptor)
        : receptor.predictedDbA;
    const thresholdDbA = inputReceptor.thresholdDbA;
    const marginDb = predictedDbA != null && thresholdDbA != null
      ? thresholdDbA - predictedDbA
      : null;
    const verdict = predictedDbA == null || thresholdDbA == null
      ? 'indeterminate' as const
      : predictedDbA <= thresholdDbA ? 'pass' as const : 'fail' as const;
    return {
      receptorId: inputReceptor.id,
      label: inputReceptor.label,
      predictedDbA,
      ...(thresholdDbA == null ? {} : { thresholdDbA }),
      verdict,
      caveat: 'Screening result only; obtain vendor octave-band data and a site-specific acoustic study.',
      distanceM: inputReceptor.distanceM,
      marginDb,
      components: receptor.components,
      modelBasis: noiseField ? 'grid-screening' as const : noiseProjection.modelBasis,
    };
  });

  const lightingFlags: string[] = [];
  if (input.lighting.fullyShielded !== true) lightingFlags.push('Specify fully shielded, downward-only fixtures.');
  if (input.lighting.bugRated !== true) lightingFlags.push('Record BUG ratings for exterior fixtures.');
  if (input.lighting.motionOrCurfewControls !== true) lightingFlags.push('Add motion or curfew controls for non-safety lighting.');
  if (input.lighting.iesPhotometricAvailable !== true) lightingFlags.push('IES photometric files are required before calculating property-line light trespass.');
  const installedPowerKW = input.lighting.fixtureCount != null && input.lighting.wattsPerFixture != null
    ? input.lighting.fixtureCount * input.lighting.wattsPerFixture / 1_000 : null;
  const installedLumens = input.lighting.fixtureCount != null && input.lighting.lumensPerFixture != null
    ? input.lighting.fixtureCount * input.lighting.lumensPerFixture : null;

  const probability = probabilityLoadServed(
    input,
    unitResults.map((row, i) => ({ netMW: row.netMWPerUnit, forcedOutageRate: input.units[i]?.forcedOutageRate })),
    criticalLoadMW,
  );

  const nMinusOneMarginMW = nMinusOneNetMW - criticalLoadMW;
  const nMinusOneReserveMarginMW = nMinusOneNetMW - reserveRequiredMW;
  const n1Verdict = verdictFor(nMinusOneMarginMW, determinate);
  const reserveVerdict = verdictFor(nMinusOneReserveMarginMW, determinate);
  const contingencyMarginMW = contingencyNetMW - criticalLoadMW;
  const contingencyReserveMarginMW = contingencyNetMW - reserveRequiredMW;
  const contingencyVerdict = verdictFor(contingencyMarginMW, determinate);
  const contingencyReserveVerdict = verdictFor(contingencyReserveMarginMW, determinate);
  const criterionLabel = contingencyOutages === 0 ? 'N' : `N-${contingencyOutages}`;
  const risks: GenerationImpactResult['risks'] = [];
  if (contingencyVerdict === 'fail') risks.push({ id: 'n-1-shortfall', severity: 'red', message: `${criterionLabel} output is ${Math.abs(contingencyMarginMW).toFixed(1)} MW below critical load.` });
  if (contingencyReserveVerdict === 'fail') risks.push({ id: 'reserve-shortfall', severity: 'red', message: `${criterionLabel} output is ${Math.abs(contingencyReserveMarginMW).toFixed(1)} MW below the separate reserve criterion.` });
  // N-1 overbuild scales with the largest contingency, not the load: a fleet
  // whose largest group is share s of normal net must install 1 / (1 - s) times
  // the firm requirement. Two equal blocks means s = 0.5 and a full spare.
  const largestContingencyShare = normalNetMW > 0 ? largestContingencyMW / normalNetMW : 0;
  if (determinate && rankedGroups.length >= 2 && largestContingencyShare >= CONTINGENCY_CONCENTRATION_SHARE) {
    risks.push({
      id: 'contingency-concentration',
      severity: 'amber',
      message: `The largest contingency group is ${(largestContingencyShare * 100).toFixed(0)}% of normal net output, so surviving ${criterionLabel} requires installing about ${(1 / (1 - largestContingencyShare)).toFixed(1)}x the firm requirement. A finer contingency granularity lowers that multiple.`,
    });
  }
  // Gas screening applies only when the fleet actually burns gas. With no
  // heat rate entered (or a non-combustion technology) peak nomination is 0,
  // so there is nothing to document against — this mirrors the constraint-fit
  // 'not-applicable' path in workflowGuidance, which previously disagreed with
  // this risk (it fired "not documented" against a zero nomination).
  const peakGasDthPerDay = peakMmbtuPerHour * HOURS_PER_DAY;
  if (peakGasDthPerDay > 0) {
    if (availableGasCapacityDthPerDay == null) risks.push({ id: 'gas-capacity-mismatch', severity: 'amber', message: 'Firm gas delivery capacity is not documented against calculated peak nomination.' });
    else if (peakGasDthPerDay > availableGasCapacityDthPerDay) risks.push({ id: 'gas-capacity-mismatch', severity: 'red', message: `Calculated peak gas nomination exceeds documented capacity by ${(peakGasDthPerDay - availableGasCapacityDthPerDay).toFixed(0)} Dth/day.` });
  }
  // Interconnect pressure derate. Applicability-guarded on the documented
  // supply record, which no pre-existing dossier carries — so every stored
  // scenario re-solves to a byte-identical risk array at the publish gate.
  // Screens a DIFFERENT bound from `gas-capacity-mismatch` above: that one
  // compares peak nomination against the nameplate/contract figure, this one
  // against what the station can physically pass at the transporter's
  // guaranteed minimum delivery pressure.
  const gasSupply = input.projectDutyBasis?.fuelGas?.supply;
  if (peakGasDthPerDay > 0 && gasSupply) {
    const derate = deriveGasInterconnectDerate(
      gasSupply,
      input.projectDutyBasis?.fuelGas?.documentedSiteSupplyMinimum,
    );
    const shortfall = derate.contractDthPerDay != null
      && derate.deratedStationDthPerDay != null
      && derate.deratedStationDthPerDay < derate.contractDthPerDay
      ? derate.contractDthPerDay - derate.deratedStationDthPerDay
      : null;
    if (derate.status === 'minimum-pressure-undocumented') {
      risks.push({
        id: 'gas-interconnect-derate',
        severity: 'amber',
        message: `Interconnect capacity is documented at ${(derate.stationDthPerDay ?? 0).toFixed(0)} Dth/day at the rated pressure, but the transporter's guaranteed minimum delivery pressure is not documented, so deliverability at minimum line pressure is not established.`,
      });
    } else if (derate.status === 'derated' && derate.deratedDeliverableDthPerDay != null) {
      const derated = derate.deratedDeliverableDthPerDay;
      if (peakGasDthPerDay > derated) {
        risks.push({
          id: 'gas-interconnect-derate',
          severity: 'red',
          message: `Peak gas nomination exceeds interconnect capacity at the documented minimum delivery pressure by ${(peakGasDthPerDay - derated).toFixed(0)} Dth/day (${derated.toFixed(0)} Dth/day derated from ${(derate.stationDthPerDay ?? 0).toFixed(0)} at the rated pressure).`,
        });
      } else if (peakGasDthPerDay > derated - derate.uncertaintyDthPerDay) {
        risks.push({
          id: 'gas-interconnect-derate',
          severity: 'amber',
          message: `Peak gas nomination sits within the ideal-gas uncertainty band of derated interconnect capacity (${derated.toFixed(0)} Dth/day +/- ${derate.uncertaintyDthPerDay.toFixed(0)}). Document real-gas compressibility or obtain the transporter's derated capacity.`,
        });
      } else if (shortfall != null) {
        risks.push({
          id: 'gas-interconnect-derate',
          severity: 'amber',
          message: `The interconnect cannot pass its own contracted maximum daily quantity at the documented minimum delivery pressure; the station derates ${shortfall.toFixed(0)} Dth/day below contract.`,
        });
      }
    }
  }
  const totalConsumption = dataCenterConsumptionGalPerDay == null ? null : generationConsumptionGalPerDay + ccsConsumptionGalPerDay + dataCenterConsumptionGalPerDay;
  if (availableWaterConsumptionGalPerDay == null || totalConsumption == null) risks.push({ id: 'water-source-mismatch', severity: 'amber', message: 'Water-source capacity or data-center consumption is missing; source adequacy is not assessed.' });
  else if (totalConsumption > availableWaterConsumptionGalPerDay) risks.push({ id: 'water-source-mismatch', severity: 'red', message: `Calculated water consumption exceeds documented capacity by ${(totalConsumption - availableWaterConsumptionGalPerDay).toFixed(0)} gal/day.` });
  if (input.ccs.enabled && (!input.ccs.pipelineExportConfirmed || ccsExportCapacityTonnesPerYear == null)) risks.push({ id: 'ccs-export-uncertainty', severity: 'amber', message: 'CO₂ export route or annual capacity is not confirmed; transport, injection, storage, and containment remain external studies.' });
  else if (input.ccs.enabled && ccsExportCapacityTonnesPerYear! < capturedCo2TonnesPerYear) risks.push({ id: 'ccs-export-uncertainty', severity: 'red', message: `Captured CO₂ exceeds documented export capacity by ${(capturedCo2TonnesPerYear - ccsExportCapacityTonnesPerYear!).toFixed(0)} tonnes/year.` });
  if (noise.some((row) => row.verdict === 'fail')) risks.push({ id: 'receptor-noise-exceedance', severity: 'red', message: 'At least one screening receptor exceeds its user-entered threshold.' });
  else if (noise.length === 0 || noise.some((row) => row.verdict === 'indeterminate')) risks.push({ id: 'receptor-noise-exceedance', severity: 'amber', message: 'Receptor noise remains unassessed because vendor source data, receptor distance, or a threshold is missing.' });
  // Planning-maturity caveat — surfaced only when no more-specific gap or
  // warning already carries it. Reserve margin + technology archetype are
  // seeded as planning-defaults on every scenario, so the previous
  // `gaps || warnings || planning-default` form fired almost always AND
  // duplicated every concrete finding it was sitting next to. Gating it on an
  // otherwise-clean result keeps it as an honest "only planning defaults
  // remain" residual instead of a catch-all echo.
  if (gaps.length === 0 && warnings.length === 0 && input.assumptions.some((assumption) => assumption.source === 'planning-default')) risks.push({ id: 'missing-vendor-permit-inputs', severity: 'amber', message: 'Planning defaults or missing vendor/permit inputs remain; complete FEED and permitting studies before commitment.' });

  return {
    schemaVersion: GENERATION_IMPACT_SCHEMA_VERSION,
    modelVersion: GENERATION_IMPACT_MODEL_VERSION,
    verdict: contingencyVerdict,
    unitResults,
    capacity: {
      normalNetMW,
      largestContingencyGroupId: largest?.[0] ?? null,
      largestContingencyMW,
      nMinusOneNetMW,
      criticalLoadMW,
      reserveRequiredMW,
      nMinusOneMarginMW,
      nMinusOneReserveMarginMW,
      nMinusOneVerdict: n1Verdict,
      nMinusOneReserveVerdict: reserveVerdict,
      contingencyOutages,
      contingencyLostMW,
      contingencyNetMW,
      contingencyMarginMW,
      contingencyReserveMarginMW,
      contingencyVerdict,
      contingencyReserveVerdict,
    },
    reliability: {
      probabilityLoadServed: probability,
      caveat: 'Independent forced-outage screening only; excludes common-cause, fuel, grid, maintenance, and repair-duration effects.',
    },
    gas: { peakMmbtuPerHour, peakDthPerDay: peakMmbtuPerHour * HOURS_PER_DAY, annualMmbtu },
    emissions: { uncontrolledCo2TonnesPerYear, capturedCo2TonnesPerYear, residualCo2TonnesPerYear, noxTonnesPerYear, coTonnesPerYear },
    water: {
      generationWithdrawalGalPerDay,
      generationConsumptionGalPerDay,
      ccsWithdrawalGalPerDay,
      ccsConsumptionGalPerDay,
      dataCenterWithdrawalGalPerDay,
      dataCenterConsumptionGalPerDay,
      totalWithdrawalGalPerDay: dataCenterWithdrawalGalPerDay == null ? null : generationWithdrawalGalPerDay + ccsWithdrawalGalPerDay + dataCenterWithdrawalGalPerDay,
      totalConsumptionGalPerDay: totalConsumption,
      ...(generationDesignDay != null
        ? { generationBasis: 'absolute-design-day' as const }
        : input.projectDutyBasis?.water?.kind === 'gross-output-intensity'
          ? { generationBasis: 'gross-output-intensity' as const }
          : {}),
    },
    footprint: {
      plantAcres,
      coolingAcres,
      switchyardAcres,
      ccsAcres,
      compressionAcres,
      totalAcres: plantAcres + coolingAcres + switchyardAcres + ccsAcres + compressionAcres,
      ...(input.footprint.installedPlantAcres == null
        ? {}
        : { plantAreaBasis: 'absolute-installed-area' as const }),
      // Conditionally spread, mirroring `plantAreaBasis` above: ABSENT for every
      // legacy scenario, so `JSON.stringify` output — and therefore resultHash —
      // is unchanged and the publish-gate replay still passes.
      ...(input.footprint.coolingAcresPerGrossMW == null
        ? {}
        : { coolingAreaBasis: 'gross-output-intensity' as const }),
      ...(input.footprint.switchyardAcresPerGrossMW == null
        ? {}
        : { switchyardAreaBasis: 'gross-output-intensity' as const }),
    },
    noise,
    // v1 radial contours are ALWAYS retained (report tables); the v2 field
    // rides alongside when the scenario opted in.
    noiseContours: noiseProjection.contours,
    noiseModelBasis: noiseField ? 'grid-screening' : noiseProjection.modelBasis,
    noiseModelVersion: noiseField ? 'grid-screening-v2' : 'free-field-screening-v1',
    noiseCaveats: noiseField ? [...noiseField.caveats] : noiseProjection.caveats,
    ...(noiseField ? { noiseField } : {}),
    lighting: { installedPowerKW, installedLumens, planningFlags: lightingFlags },
    gaps,
    warnings,
    risks,
  };
}

export function solveGenerationImpact(input: GenerationImpactScenarioInput): GenerationImpactResult {
  return solveWorkbenchGenerationImpact(input);
}
